import {
  Button,
  IconImages,
  IconTrashCan,
  IndigoIndigo60,
  Spinner,
  useNotifications,
  Box,
  Flex
} from '@sequencehq/core-components'
import type { ServerAsset } from '@sequencehq/api/utils/commonModels'
import {
  COVER_IMAGE_ACCEPTED_TYPES,
  useSelectCoverImageSrc,
  useSelectSignedCoverImageSrc,
  useStoreCoverImageAsset
} from 'Cube/domain/subdomain/coverImage'
import { UploadAssetButton } from 'Assets/index'
import { FC, PropsWithChildren, useMemo } from 'react'
import { useCoverImageEditorContext } from './CoverImageEditorContext'
import { constructAssetPath } from '@sequencehq/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useQuery } from '@sequencehq/api'

const TRANSITION_DURATION = 150

type CoverImageEditorUploadButtonProps = {
  text?: string
  'data-testid'?: string
}

export const CoverImageEditorUploadButton = ({
  text = 'Add cover',
  'data-testid': dataTestId
}: CoverImageEditorUploadButtonProps) => {
  const storeAsset = useStoreCoverImageAsset()
  const { displayNotification } = useNotifications()
  const { data: sequenceAccountSettings } = useQuery(
    dashboard20240730Client.getSequenceAccountSettings
  )

  const { setFile, setImageSrc, prevImageSrc } = useCoverImageEditorContext()

  const handleFilePicked = (pickedFiles: File[]) => {
    const pickedFile = pickedFiles.at(0)
    if (!pickedFile) return

    setFile(pickedFile)
    setImageSrc(fileToImageSrc(pickedFile))
  }

  const handleUploadSuccess = (serverAsset: ServerAsset) => {
    if (!sequenceAccountSettings) {
      handleUploadFail('Unable to get account ID')
      return
    }

    setFile(undefined)
    storeAsset(
      constructAssetPath({
        accountId: sequenceAccountSettings.sequenceAccountId,
        assetId: serverAsset.id
      })
    )
  }

  const handleUploadFail = (error: string) => {
    setFile(undefined)
    setImageSrc(prevImageSrc)

    displayNotification(`Upload failed: ${error}`, {
      type: 'error'
    })
  }

  return (
    <ShowOnGroupHover>
      <UploadAssetButton
        leadingIcon={<IconImages />}
        variant="secondary"
        onFilesPicked={handleFilePicked}
        onUploadSuccess={handleUploadSuccess}
        onUploadFailure={handleUploadFail}
        accept={COVER_IMAGE_ACCEPTED_TYPES}
        data-testid={dataTestId}
      >
        {text}
      </UploadAssetButton>
    </ShowOnGroupHover>
  )
}

export const CoverImageEditor: FC = () => {
  const assetSrc = useSelectCoverImageSrc()
  const storeAsset = useStoreCoverImageAsset()
  const { file, imageSrc, setImageSrc, isDisabled } =
    useCoverImageEditorContext()
  const signedImageSrc = useSelectSignedCoverImageSrc()

  const removeCoverImage = () => {
    storeAsset(null)
    setTimeout(() => {
      setImageSrc(undefined)
    }, TRANSITION_DURATION)
  }

  const showImage = !!assetSrc || !!file

  const showControls = !!assetSrc && !file && !isDisabled

  const backgroundImage = useMemo(() => {
    if (imageSrc?.startsWith('blob:')) {
      return `url(${imageSrc})`
    }

    if (signedImageSrc) {
      return `url(${signedImageSrc})`
    }

    return undefined
  }, [imageSrc, signedImageSrc])

  return (
    <Box position="relative" role="group">
      <Flex
        transition={`height ${TRANSITION_DURATION}ms ease-out, opacity ${TRANSITION_DURATION}ms ease-out`}
        position="relative"
        justifyContent="center"
        alignItems="center"
        height={showImage ? '240px' : 0}
        opacity={showImage ? 1 : 0}
        backgroundImage={backgroundImage}
        backgroundSize="100% auto"
        backgroundPosition="50% 50%"
        backgroundRepeat="no-repeat"
        mt={!imageSrc ? 3 : undefined}
      >
        {!!file && (
          <Spinner color={IndigoIndigo60} width="36px" height="36px" />
        )}
        <Box opacity={showControls ? 1 : 0}>
          <ShowOnGroupHover>
            <Flex gap="4px" style={{ position: 'absolute', top: 8, right: 8 }}>
              <CoverImageEditorUploadButton
                text="Replace cover"
                data-testid="quote.coverImage.buttonReplace"
              />
              <Button
                variant="secondary"
                icon={<IconTrashCan />}
                aria-label="remove cover image"
                onClick={removeCoverImage}
                data-testid="quote.coverImage.buttonRemove"
              />
            </Flex>
          </ShowOnGroupHover>
        </Box>
      </Flex>
    </Box>
  )
}

const ShowOnGroupHover: FC<PropsWithChildren> = ({ children }) => (
  <Box
    _groupHover={{ opacity: 1 }}
    opacity={0}
    transition="opacity 0.15s ease-out"
  >
    {children}
  </Box>
)

function fileToImageSrc(file: File): string {
  return URL.createObjectURL(file)
}
